import React, { useState } from 'react';
import { Shield, User, Lock, Send, CheckCircle, AlertCircle, Copy, ExternalLink, Eye, EyeOff } from 'lucide-react';

const AdminTestPage: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [targetEmail, setTargetEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
    registration_url?: string;
    plan?: string;
    amount?: number;
  } | null>(null);
  const [error, setError] = useState('');

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password === 'forceai2025') {
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('パスワードが正しくありません');
    }
  };

  const handleGrantSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!targetEmail.trim()) {
      setError('テスト用アカウントのメールアドレスを入力してください');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await fetch('/api/admin/grant-test-premium', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          admin_password: 'forceai2025',
          target_email: targetEmail,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setResult(data);
        setTargetEmail(''); // 成功時にフォームをクリア
      } else {
        setError(data.error || 'エラーが発生しました');
      }
    } catch (error) {
      setError('サーバーとの通信に失敗しました');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('クリップボードのコピーに失敗しました:', err);
    }
  };

  const openRegistrationUrl = (url: string) => {
    window.open(url, '_blank');
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* パスワード認証ヘッダー */}
            <div className="bg-red-600 px-6 py-4">
              <div className="flex items-center">
                <Lock className="h-6 w-6 text-white mr-3" />
                <h1 className="text-xl font-bold text-white">管理者認証</h1>
              </div>
              <p className="text-red-100 text-sm mt-1">
                パスワードを入力してください
              </p>
            </div>

            {/* パスワード入力フォーム */}
            <form onSubmit={handlePasswordSubmit} className="p-6 space-y-6">
              <div className="space-y-2">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  管理者パスワード
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Shield className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full pl-10 pr-12 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    placeholder="パスワードを入力"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <div className="flex items-center p-4 bg-red-50 border border-red-200 rounded-md">
                  <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                  <span className="text-red-800 text-sm">{error}</span>
                </div>
              )}

              <button
                type="submit"
                className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <Lock className="mr-2 h-4 w-4" />
                認証
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* ヘッダー */}
          <div className="bg-red-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Shield className="h-6 w-6 text-white mr-3" />
                <h1 className="text-xl font-bold text-white">テスト用プレミアムプラン付与</h1>
              </div>
              <button
                onClick={() => {
                  setIsAuthenticated(false);
                  setPassword('');
                  setTargetEmail('');
                  setResult(null);
                  setError('');
                }}
                className="text-red-100 hover:text-white text-sm underline"
              >
                ログアウト
              </button>
            </div>
            <p className="text-red-100 text-sm mt-1">
              認証済み - テスト用アカウントを作成できます
            </p>
          </div>

          {/* フォーム */}
          <form onSubmit={handleGrantSubmit} className="p-6 space-y-6">
            <div className="space-y-2">
              <label htmlFor="targetEmail" className="block text-sm font-medium text-gray-700">
                テスト用アカウントのメールアドレス
              </label>
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="targetEmail"
                  type="email"
                  value={targetEmail}
                  onChange={(e) => setTargetEmail(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="test@example.com"
                  required
                />
              </div>
            </div>

            {error && (
              <div className="flex items-center p-4 bg-red-50 border border-red-200 rounded-md">
                <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                <span className="text-red-800 text-sm">{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  処理中...
                </span>
              ) : (
                <span className="flex items-center">
                  <Send className="mr-2 h-4 w-4" />
                  テスト用プレミアムプランを付与
                </span>
              )}
            </button>
          </form>

          {/* 成功結果 */}
          {result && result.success && (
            <div className="px-6 pb-6">
              <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-400 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-green-800 mb-2">
                      {result.message}
                    </h3>
                    
                    <div className="space-y-3">
                      <div className="bg-white rounded-md p-3 border border-green-200">
                        <div className="text-xs text-gray-600 mb-1">プラン情報</div>
                        <div className="text-sm">
                          <span className="font-medium">プラン:</span> {result.plan || 'premium_test'} 
                          <span className="ml-4 font-medium">料金:</span> ¥{result.amount?.toLocaleString()}/月
                        </div>
                      </div>
                      
                      {result.registration_url && (
                        <div className="bg-white rounded-md p-3 border border-green-200">
                          <div className="text-xs text-gray-600 mb-2">登録URL</div>
                          <div className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={result.registration_url}
                              readOnly
                              className="flex-1 text-xs bg-gray-50 border border-gray-200 rounded px-2 py-1 font-mono"
                            />
                            <button
                              onClick={() => copyToClipboard(result.registration_url!)}
                              className="p-1 text-gray-500 hover:text-gray-700 rounded"
                              title="コピー"
                            >
                              <Copy className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => openRegistrationUrl(result.registration_url!)}
                              className="p-1 text-gray-500 hover:text-gray-700 rounded"
                              title="新しいタブで開く"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 説明 */}
        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <h3 className="text-sm font-medium text-yellow-800 mb-2">使用方法</h3>
          <ol className="text-sm text-yellow-700 space-y-1 list-decimal list-inside">
            <li>管理者パスワードでログイン</li>
            <li>テスト用アカウントのメールアドレスを入力</li>
            <li>「テスト用プレミアムプランを付与」ボタンをクリック</li>
            <li>生成された登録URLをテストユーザーに送信</li>
            <li>テストユーザーが登録URLからアカウント作成を完了</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default AdminTestPage;